import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { PartyIdentificationType } from '@nano/common/party-identification';
import { joinFullName, splitFullName } from '@nano/common/util';

export interface BeneficiaryNameControls {
  beneficiaryName: AbstractControl;
  name: AbstractControl;
  surname: AbstractControl;
  identification: AbstractControl;
}

export interface BeneficiaryNameAvailability {
  name: boolean;
  surname: boolean;
}

@Injectable()
export class BeneficiaryNameSyncService {
  private readonly destroyRef = inject(DestroyRef);

  private controls?: BeneficiaryNameControls;
  private syncing = false;

  connect(controls: BeneficiaryNameControls): void {
    if (!controls.beneficiaryName || !controls.name || !controls.surname || !controls.identification) {
      return;
    }

    this.controls = controls;

    controls.beneficiaryName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((beneficiaryName: string) => this.applyBeneficiaryName(beneficiaryName));

    [controls.name, controls.surname].forEach((control) =>
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applyNameParts())
    );
  }

  apply(identification: PartyIdentificationType, availability: BeneficiaryNameAvailability = { name: true, surname: true }): void {
    if (!this.controls) {
      return;
    }

    const beneficiaryName = this.controls.beneficiaryName.value as string;

    this.write(() => {
      if (!this.renderParts(identification, availability)) {
        return;
      }

      if (this.splitsForSelectedIdentification(identification, availability.surname)) {
        const { name, surname } = splitFullName(beneficiaryName);
        this.controls.name.setValue(name);
        this.controls.surname.setValue(surname);
        return;
      }

      this.controls.name.setValue(beneficiaryName || null);
    });
  }

  applyRenderSwitch(identification: PartyIdentificationType, availability: BeneficiaryNameAvailability): void {
    if (!this.controls) {
      return;
    }

    this.write(() => this.renderParts(identification, availability));
  }

  private renderParts(identification: PartyIdentificationType, availability: BeneficiaryNameAvailability): boolean {
    if (!availability.name) {
      this.hidePart(this.controls.name);
      this.hidePart(this.controls.surname);
      return false;
    }

    this.showPart(this.controls.name);

    if (this.splitsForSelectedIdentification(identification, availability.surname)) {
      this.showPart(this.controls.surname);
      return true;
    }

    this.hidePart(this.controls.surname);
    return true;
  }

  private showPart(control: AbstractControl): void {
    if (control.disabled) {
      control.enable();
    }
  }

  private hidePart(control: AbstractControl): void {
    if (control.disabled && control.value === null) {
      return;
    }

    control.reset(null);
    control.disable();
  }

  private splitsForSelectedIdentification(identification: PartyIdentificationType, surnameOffered: boolean): boolean {
    return identification === PartyIdentificationType.PRIVATE && surnameOffered;
  }

  private applyBeneficiaryName(beneficiaryName: string): void {
    if (!this.canSync()) {
      return;
    }

    this.write(() => {
      if (!this.isSplit()) {
        this.controls.name.setValue(beneficiaryName || null);
        return;
      }

      const { name, surname } = splitFullName(beneficiaryName);
      this.controls.name.setValue(name);
      this.controls.surname.setValue(surname);
    });
  }

  private applyNameParts(): void {
    if (!this.canSync()) {
      return;
    }

    const name = this.controls.name.value as string;
    const surname = this.controls.surname.value as string;
    const parts = this.isSplit() ? [name, surname] : [name];

    if (parts.some((part) => !part?.trim())) {
      return;
    }

    this.write(() => this.controls.beneficiaryName.setValue(joinFullName(...parts)));
  }

  private canSync(): boolean {
    return !this.syncing && !!this.controls?.identification.value && this.controls.name.enabled;
  }

  private isSplit(): boolean {
    return this.splitsForSelectedIdentification(this.controls?.identification.value as PartyIdentificationType, this.controls.surname.enabled);
  }

  private write(update: () => void): void {
    this.syncing = true;
    try {
      update();
    } finally {
      this.syncing = false;
    }
  }
}
